// src/services/chatPersistenceService.ts
import { supabase } from "@/integrations/supabase/client";
import { ChatMessage } from "@/components/agent/chat/types";

// Define the database message format based on actual schema
interface DatabaseMessage {
  id: string;
  content: string;
  conversation_id: string;
  created_at: string;
  sender_type: "user" | "ai" | "agent_chat_user";
  sender_id?: string | null;
  openai_message_id?: string | null;
  metadata?: {
    is_system_message?: boolean;
    responseId?: string;
    [key: string]: unknown;
  } | null;
}

export const chatPersistenceService = {
  storeConversationId(agentPublicId: string, conversationId: string): void {
    //console.log(
    //  `[PERSISTENCE] Storing conversation ID ${conversationId} for agent ${agentPublicId}`,
    //);
    localStorage.setItem(`public_chat_${agentPublicId}`, conversationId);
    // Also store timestamp of last interaction
    localStorage.setItem(
      `public_chat_${agentPublicId}_timestamp`,
      new Date().toISOString(),
    );
    //console.log(
    //  `[PERSISTENCE] localStorage keys after storing:`,
    //  Object.keys(localStorage).filter((key) => key.startsWith("public_chat_")),
    //);
  },

  getStoredConversationId(agentPublicId: string): string | null {
    const storedId = localStorage.getItem(`public_chat_${agentPublicId}`);
    //console.log(
    //  `[PERSISTENCE] Retrieved conversation ID ${storedId} for agent ${agentPublicId}`,
    //);
    if (!storedId) {
      //console.log(
      //  `[PERSISTENCE] No stored conversation found for agent ${agentPublicId}`,
      //);
      console.error(
        `[PERSISTENCE] No stored conversation found for agent ${agentPublicId}`,
      );
    }
    return storedId;
  },

  async fetchMessages(
    conversationId: string,
    limit = 50,
    offset = 0,
  ): Promise<{
    messages: ChatMessage[];
    hasMore: boolean;
    error: Error | null;
  }> {
    //console.log(
    //  `[PERSISTENCE] Fetching messages for conversation ${conversationId} with limit=${limit}, offset=${offset}`,
    // );
    try {
      // First check if offset is valid by getting count
      const { count, error: countError } = await supabase
        .from("messages")
        .select("*", { count: "exact", head: true })
        .eq("conversation_id", conversationId);

      //console.log(
      //  `[PERSISTENCE] Total message count for conversation ${conversationId}: ${count}`,
      //  );

      if (countError) {
        console.error(`[PERSISTENCE] Error getting message count:`, countError);
        throw countError;
      }

      // If offset is beyond available messages, return empty result
      if (count !== null && offset >= count) {
        //console.log(
        //  `[PERSISTENCE] Requested offset ${offset} exceeds available messages (${count})`,
        //);
        return {
          messages: [],
          hasMore: false,
          error: null,
        };
      }

      const { data, error } = await supabase
        .from("messages")
        .select("*")
        .eq("conversation_id", conversationId)
        .order("created_at", { ascending: true })
        .range(offset, offset + limit - 1);

      if (error) {
        console.error(`[PERSISTENCE] Error fetching messages:`, error);
        throw error;
      }

      //console.log(
      //  `[PERSISTENCE] Fetched ${data.length} messages from conversation ${conversationId}`,
      //);
      //console.log(
      //  `[PERSISTENCE] First message:`,
      //  data.length > 0 ? data[0] : "No messages",
      //);
      //console.log(
      //  `[PERSISTENCE] Last message:`,
      //  data.length > 0 ? data[data.length - 1] : "No messages",
      //);

      // Cast data to DatabaseMessage array to get correct types
      const dbMessages = data as unknown as DatabaseMessage[];

      const messages = dbMessages.map((msg) => {
        // Map database sender_type to UI message type
        let messageType: "user" | "agent" | "system";

        if (
          msg.sender_type === "user" ||
          msg.sender_type === "agent_chat_user"
        ) {
          messageType = "user";
        } else if (msg.sender_type === "ai") {
          // Check if it's a system message
          if (
            msg.metadata &&
            typeof msg.metadata === "object" &&
            msg.metadata.is_system_message
          ) {
            messageType = "system";
          } else {
            messageType = "agent";
          }
        } else {
          // Default fallback
          //console.log(
          //  `[PERSISTENCE] Unknown sender_type: ${msg.sender_type}, defaulting to "agent"`,
          //);
          messageType = "agent";
        }

        return {
          type: messageType,
          content: msg.content,
          timestamp: msg.created_at || new Date().toISOString(),
          openaiMessageId: msg.openai_message_id || undefined,
        };
      });

      return {
        messages,
        hasMore: count ? count > offset + limit : false,
        error: null,
      };
    } catch (error) {
      console.error("[PERSISTENCE] Error fetching messages:", error);
      return {
        messages: [],
        hasMore: false,
        error:
          error instanceof Error
            ? error
            : new Error("Unknown error fetching messages"),
      };
    }
  },

  clearConversationData(agentPublicId: string): void {
    //console.log(
    //  `[PERSISTENCE] Clearing conversation data for agent ${agentPublicId}`,
    //);
    localStorage.removeItem(`public_chat_${agentPublicId}`);
    localStorage.removeItem(`public_chat_${agentPublicId}_timestamp`);
  },

  // ===== INTERNAL CHAT PERSISTENCE (new functionality) =====

  /**
   * Store the last viewed internal conversation for cross-device persistence
   * Uses localStorage as primary storage with database fallback
   */
  storeInternalConversationId(
    userId: string,
    agentId: string,
    conversationId: string,
  ): void {
    //console.log(
    //  `[INTERNAL-PERSISTENCE] Storing conversation ID ${conversationId} for user ${userId} + agent ${agentId}`,
    //);
    const key = `internal_chat_${userId}_${agentId}`;
    localStorage.setItem(key, conversationId);
    localStorage.setItem(`${key}_timestamp`, new Date().toISOString());

    //console.log(
    //  `[INTERNAL-PERSISTENCE] localStorage keys after storing:`,
    //  Object.keys(localStorage).filter((key) =>
    //    key.startsWith("internal_chat_"),
    //  ),
    //);
  },

  /**
   * Get the stored internal conversation ID for a user-agent combination
   */
  getStoredInternalConversationId(
    userId: string,
    agentId: string,
  ): string | null {
    const key = `internal_chat_${userId}_${agentId}`;
    const storedId = localStorage.getItem(key);
    //console.log(
    //  `[INTERNAL-PERSISTENCE] Retrieved conversation ID ${storedId} for user ${userId} + agent ${agentId}`,
    //);
    return storedId;
  },

  /**
   * Clear internal conversation data for a user-agent combination
   */
  clearInternalConversationData(userId: string, agentId: string): void {
    //console.log(
    //  `[INTERNAL-PERSISTENCE] Clearing internal conversation data for user ${userId} + agent ${agentId}`,
    //);
    const key = `internal_chat_${userId}_${agentId}`;
    localStorage.removeItem(key);
    localStorage.removeItem(`${key}_timestamp`);
  },

  /**
   * Validate that a stored internal conversation ID still exists and belongs to the user
   */
  async validateStoredInternalConversation(
    userId: string,
    agentId: string,
    conversationId: string,
  ): Promise<boolean> {
    //console.log(
    //  `[INTERNAL-PERSISTENCE] Validating conversation ${conversationId} for user ${userId} + agent ${agentId}`,
    //);

    try {
      const { data, error } = await supabase
        .from("conversations")
        .select("id, user_id, agent_id, state")
        .eq("id", conversationId)
        .eq("user_id", userId)
        .eq("agent_id", agentId)
        .is("agent_chat_user_id", null) // Ensure it's an internal conversation
        .single();

      if (error || !data) {
        //console.log(
        //  `[INTERNAL-PERSISTENCE] Stored conversation ${conversationId} is invalid or doesn't exist`,
        //);
        // Clear invalid localStorage entry
        this.clearInternalConversationData(userId, agentId);
        return false;
      }

      //console.log(
      //  `[INTERNAL-PERSISTENCE] Conversation ${conversationId} is valid`,
      //);
      return true;
    } catch (error) {
      console.error(
        `[INTERNAL-PERSISTENCE] Error validating conversation:`,
        error,
      );
      this.clearInternalConversationData(userId, agentId);
      return false;
    }
  },

  /**
   * Get the most recently active internal conversation for a user-agent combination
   * This considers the conversation with the most recent message activity
   */
  async getMostRecentInternalConversation(
    userId: string,
    agentId: string,
  ): Promise<string | null> {
    //console.log(
    //  `[INTERNAL-PERSISTENCE] Finding most recent conversation for user ${userId} + agent ${agentId}`,
    //);

    try {
      const { data, error } = await supabase
        .from("conversations")
        .select("id, updated_at")
        .eq("user_id", userId)
        .eq("agent_id", agentId)
        .is("agent_chat_user_id", null) // Ensure it's an internal conversation
        .order("updated_at", { ascending: false })
        .limit(1)
        .single();

      if (error || !data) {
        //console.log(
        //  `[INTERNAL-PERSISTENCE] No conversations found for user ${userId} + agent ${agentId}`,
        //);
        return null;
      }

      //  console.log(
      //  `[INTERNAL-PERSISTENCE] Most recent conversation: ${data.id} (updated: ${data.updated_at})`,
      //);
      return data.id;
    } catch (error) {
      console.error(
        `[INTERNAL-PERSISTENCE] Error finding most recent conversation:`,
        error,
      );
      return null;
    }
  },
};
